import { lazily } from 'react-lazily'

/**
 * Layouts
 */
import Guard from 'guards/Guard'
import { Dashboard, Centered } from 'layout'

/**
 * Dashboard Pages
 */

import errorRoutes from 'routes/routes-by-role/error-routes'
import cajaRoutes from 'routes/routes-by-role/caja-routes'
import oraculoRoutes from 'routes/routes-by-role/oraculo-routes'
import adminRoutes from 'routes/routes-by-role/admin-routes'
import educacionRoutes from 'routes/routes-by-role/educacion-routes'
import passwordRecoveryRoutes from 'routes/routes-by-role/password-recovery-routes'
import newUserRegistrationRoutes from 'routes/routes-by-role/new-user-registration-routes'
import helpDeskRoutes from 'routes/routes-by-role/help-desk-routes'
import reportesRoutes from 'routes/routes-by-role/reportes-routes'
/**
 * Constants
 */
import { HOME_FOR_ROLE_ROUTE_PREFIX } from 'constants'

/**
 * Login Page
 */
const { Login, SignInForm, Validate2FA } = lazily(() => import('pages/login'))

/**
 * Base Route Paths for each Module
 */
export const MODULES_BASE_ROUTES = {
  login: '/',
  home: '/dashboard',
  alumnos: '/dashboard/alumnos',
  aprobaciones: '/dashboard/aprobaciones',
}

/**
 * Layouts
 */
const LAYOUTS = {
  centered: Centered,
  dashboard: Dashboard,
}

/**
 * ROUTES array
 * This array contains the routes of the application can have the following keys
 * name: {string} The name of the route, used for generating the route name map
 * path: {string|null} The path of the route, used for generating the route name map, if used in a child route, the path will be appended to the parent path
 * layoutRoute: {boolean} The layout route dont have a path, will render all childs inside the layout in an <Outled> component
 * element: {React.Component} The component to render for the route
 * layout: {React.Component} Component The layout component to be used for the route, element will be rendered inside this layout
 * guard: {React.Component} Component The guard component to be used for the route, if the guard component returns false, the route will navigate to login if no user or 403 error page
 * layoutProps: {Object} The _props to be passed to the layout component
 * elementProps: {Object} The _props to be passed to the element component
 * guardProps: {Object} The _props to be passed to the guard component
 * childs: {Array} Array of child routes, child routes will have the same options as the main ones
 * suspenceFallback: {React.Component} The fallback component to be used when the route is loading
 */
export const ROUTES = [
  ...errorRoutes,
  {
    name: 'signin',
    path: MODULES_BASE_ROUTES.login,
    element: Login,
    layout: LAYOUTS.centered,
    childs: [
      {
        name: 'signin-form',
        path: '',
        element: SignInForm,
      },
      {
        name: 'validate-2fa',
        path: 'validate-2fa',
        element: Validate2FA,
      },
      {
        name: 'validate-2fa-with-code',
        path: 'validate-2fa/:code',
        element: Validate2FA,
      },
      ...passwordRecoveryRoutes,
      ...newUserRegistrationRoutes,
    ],
  },
  {
    name: 'dashboard layout',
    element: Dashboard,
    guard: Guard,
    childs: [
      ...educacionRoutes,
      ...cajaRoutes,
      ...adminRoutes,
      ...oraculoRoutes,
      ...helpDeskRoutes,
      ...reportesRoutes
    ],
  },
]

/**
 * Builds an object with route name as keys and route path as value from The ROUTES array
 * This function will be used recursevily to add the child routes as well
 * @param {Array} routes The routes array
 * @param {(String|Null)} baseRoute The base route path of the parent route, on initial call this should be null
 * @returns {Object} Object with route name as keys and route path as value
 */
function buildRouteNameMap(routes, parentPath = null) {
  return routes.reduce((acc, route) => {
    if (!Boolean(route?.layoutRoute)) {
      acc[route.name] = Boolean(parentPath)
        ? (parentPath + '/' + route.path).replace('//', '/')
        : route.path
      if (route?.homeForRole) {
        route?.homeForRole.map((role) => {
          acc[HOME_FOR_ROLE_ROUTE_PREFIX + role.replaceAll(" ","-").toLowerCase()] = acc[route.name]
        })
      }
    }
    if (route.childs) {
      acc = {
        ...acc,
        ...buildRouteNameMap(
          route.childs,
          Boolean(parentPath)
            ? (parentPath + '/' + route.path).replace('//', '/')
            : route.path
        ),
      }
    }
    return acc
  }, {})
}
// Create the route name map
const namedRoutes = buildRouteNameMap(ROUTES)

/**
 *
 * @param {String} routeName
 * @param {String|null} params
 * @returns {String} The route path for the given route name or "/" if the route name is not found
 */
export function getRoutePath(routeName, params = null) {
  if (!Boolean(namedRoutes?.[routeName])) {
    //If route name is not found, return the default route and log the error
    return '/'
  }
  let returnPath = namedRoutes[routeName].split('/:')
  if (returnPath.length > 1 && Boolean(params)) {
    //Check if route requires a param and if there is no param, return the default route and log the error
    return returnPath[0] + '/' + params
  }

  return returnPath[0]
}

export function getHomeRouteForRole(role) {
  if (!Boolean(role)) {
    return '/'
  }
  let homeRouteName = HOME_FOR_ROLE_ROUTE_PREFIX + role.replaceAll(" ","-").toLowerCase()
  return getRoutePath(homeRouteName)
}
